import { Link, useLocation } from "react-router-dom";
import { useState, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { ChevronDown, Menu, X, ChevronRight, UserCircle, LogIn, LogOut, BarChart2, Settings, FileText, Inbox, ArrowRight, Home, Camera, MessageCircle, Lock, Calendar, Megaphone, Sofa, Info, HelpCircle, LifeBuoy } from "lucide-react";
import EnrichmentModal from "@/pages/manchester/EnrichmentModal";
import BookCallModal from "@/components/BookCallModal";
import GuaranteedRentClosedModal from "@/pages/guaranteed-rent/GuaranteedRentClosedModal";
import LocationsMegaMenu from "./LocationsMegaMenu";
import MobileLocationsAccordion from "./MobileLocationsAccordion";
import MoreMegaMenu from "./MoreMegaMenu";
import SolutionsMenu from "./SolutionsMenu";
import { FEATURE_MENU } from "@/pages/management/features/featureData";
import ResourcesMegaMenu from "./ResourcesMegaMenu";
import { base44 } from "@/api/base44Client";

const navLinks = [
  { label: "Locations", path: "/locations", hasDropdown: true },
  { label: "Solutions", path: "/management", hasSolutionsDropdown: true },
  { label: "Resources", path: "/resources", hasResourcesDropdown: true },
  { label: "More", path: "/more", hasMoreDropdown: true },
];

const moreLinksMobile = [
  { icon: Sofa, label: "Interiors & Staging", path: "/interiors" },
  { icon: Info, label: "About Us", path: "/about" },
  { icon: MessageCircle, label: "Contact", path: "/contact" },
  { icon: HelpCircle, label: "FAQs", path: "/faqs" },
  { icon: LifeBuoy, label: "Support", path: "/support" },
  { icon: FileText, label: "Resources", path: "/resources" },
];

const SOLUTION_ICONS = { Home, Camera, MessageCircle, Lock, Calendar, BarChart2, Megaphone };

const mobileRowStyle = {
  display: "flex", alignItems: "center", gap: 10,
  fontFamily: "'DM Sans', sans-serif", fontSize: 15, fontWeight: 500,
  color: "#1A1A1A", textDecoration: "none",
  padding: "10px 12px", borderRadius: 8, transition: "background 0.15s",
};
const mobileIconBox = { width: 24, height: 24, borderRadius: 8, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 };

const LOGO_DARK = "https://media.base44.com/images/public/6a316287a1be7d9945ac7d10/4e392029e_TruestaysLogoDark.png";

export default function Navbar() {
  const location = useLocation();
  const [solid, setSolid] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showBookCall, setShowBookCall] = useState(false);
  const [showGrModal, setShowGrModal] = useState(false);
  const [locationsOpen, setLocationsOpen] = useState(false);
  const [moreOpen, setMoreOpen] = useState(false);
  const [resourcesOpen, setResourcesOpen] = useState(false);
  const [solutionsOpen, setSolutionsOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [locationsExpanded, setLocationsExpanded] = useState(false);
  const [mobileLocationsOpen, setMobileLocationsOpen] = useState(false);
  const [mobileMoreOpen, setMobileMoreOpen] = useState(false);
  const [mobileSolutionsOpen, setMobileSolutionsOpen] = useState(false);
  const [mobileAdminOpen, setMobileAdminOpen] = useState(false);
  const [user, setUser] = useState(null);
  const [accountOpen, setAccountOpen] = useState(false);
  const accountCloseTimer = useRef(null);
  const moreCloseTimer = useRef(null);
  const locCloseTimer = useRef(null);
  const resCloseTimer = useRef(null);
  const solCloseTimer = useRef(null);

  useEffect(() => {
    base44.auth.me().then(setUser).catch(() => setUser(null));
  }, []);

  const openAccount = () => { clearTimeout(accountCloseTimer.current); setAccountOpen(true); };
  const scheduleCloseAccount = () => { accountCloseTimer.current = setTimeout(() => setAccountOpen(false), 120); };

  const openMore = () => { clearTimeout(moreCloseTimer.current); setMoreOpen(true); };
  const scheduleCloseMore = () => { moreCloseTimer.current = setTimeout(() => setMoreOpen(false), 120); };
  const openLocations = () => { clearTimeout(locCloseTimer.current); setLocationsOpen(true); };
  const scheduleCloseLocations = () => { locCloseTimer.current = setTimeout(() => setLocationsOpen(false), 120); };
  const openResources = () => { clearTimeout(resCloseTimer.current); setResourcesOpen(true); };
  const scheduleCloseResources = () => { resCloseTimer.current = setTimeout(() => setResourcesOpen(false), 120); };
  const openSolutions = () => { clearTimeout(solCloseTimer.current); setSolutionsOpen(true); };
  const scheduleCloseSolutions = () => { solCloseTimer.current = setTimeout(() => setSolutionsOpen(false), 120); };

  useEffect(() => {
    setSolid(false);
    function updateNav() {
      setSolid(window.scrollY > 10);
    }
    window.addEventListener("scroll", updateNav, { passive: true });
    updateNav();
    return () => window.removeEventListener("scroll", updateNav);
  }, [location.pathname]);

  useEffect(() => {
    const handleClickOutside = () => {
      setLocationsOpen(false);
      setMoreOpen(false);
      setResourcesOpen(false);
      setSolutionsOpen(false);
      setMobileMenuOpen(false);
      setLocationsExpanded(false);
      setMobileLocationsOpen(false);
      setMobileMoreOpen(false);
      setMobileSolutionsOpen(false);
      setMobileAdminOpen(false);
    };
    if (locationsOpen || mobileMenuOpen || moreOpen || resourcesOpen || solutionsOpen) {
      document.addEventListener("click", handleClickOutside);
      return () => document.removeEventListener("click", handleClickOutside);
    }
  }, [locationsOpen, mobileMenuOpen, moreOpen, resourcesOpen]);

  const scrollToForm = (e) => {
    e.preventDefault();
    const form = document.getElementById("main-form");
    if (form) form.scrollIntoView({ behavior: "smooth", block: "center" });
  };

  const isHome = location.pathname === "/";
  const isResources = location.pathname.startsWith("/resources");
  const isGuaranteedRent = location.pathname.startsWith("/guaranteed-rent");
  const isManagement = location.pathname.startsWith("/management");
  const isSolutionsFeature = ["property-management","listings-and-pricing","guest-communication","screening-and-security","bookings-and-payments","performance-reporting","marketing-and-platforms"].some(s => location.pathname === `/management/${s}` || location.pathname === `/management/${s}/`);
  const onDarkHero = (isHome || isResources || isManagement) && !solid && !isSolutionsFeature;
  const forceSolid = isSolutionsFeature;

  const bg = (solid || forceSolid) ? "rgba(255,255,255,0.82)" : "transparent";
  const navFilter = (solid || forceSolid) ? "drop-shadow(0 1px 0 rgba(0,0,0,0.08))" : "none";
  const backdropBlur = (solid || forceSolid) ? "blur(16px)" : "none";
  const linkColor = onDarkHero ? "rgba(255,255,255,0.85)" : "#1A1A1A";
  const linkHoverColor = onDarkHero ? "#fff" : "#1A1A1A";
  const loginColor = onDarkHero ? "rgba(255,255,255,0.85)" : "#1A1A1A";
  const logoFilter = onDarkHero ? "brightness(0) invert(1)" : "none";
  const mobileIconColor = onDarkHero ? "#fff" : "#1A1A1A";

  return (
    <nav style={{
      position: "fixed", top: "var(--ts-promo-height, 0px)", left: 0, right: 0, zIndex: 200,
      height: 68,
      overflow: "visible",
      background: bg,
      filter: navFilter,
      backdropFilter: backdropBlur,
      WebkitBackdropFilter: backdropBlur,
      transition: "background 0.3s, filter 0.3s, backdrop-filter 0.3s",
    }}>
      <div style={{ maxWidth: 1400, margin: "0 auto", height: "100%", display: "flex", alignItems: "center", justifyContent: "space-between", paddingLeft: "clamp(16px, 5vw, 40px)", paddingRight: "clamp(16px, 5vw, 40px)", width: "100%", boxSizing: "border-box" }}>
        {/* Logo + nav links grouped to the left (Ramp-style) */}
        <div style={{ display: "flex", alignItems: "center", gap: 28 }} className="nav-left-group">
          <Link to="/" style={{ textDecoration: "none" }} onClick={() => setMobileMenuOpen(false)}>
            <img
              src={LOGO_DARK}
              alt="Truestays"
              width="105"
              height="26"
              style={{ height: 26, width: "auto", filter: logoFilter, transition: "filter 0.3s" }}
            />
          </Link>

          {/* Nav links */}
          <div style={{ display: "flex", alignItems: "center", gap: 32 }} className="nav-links-desktop">
            {navLinks.map((link) =>
              link.hasSolutionsDropdown ? (
                 <div key={link.path} style={{ position: "relative" }} onMouseEnter={openSolutions} onMouseLeave={scheduleCloseSolutions} onClick={e => e.stopPropagation()}>
                   <button
                     onClick={() => setSolutionsOpen(!solutionsOpen)}
                     style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 14, fontWeight: 400, color: linkColor, textDecoration: "none", transition: "color 0.2s", background: "none", border: "none", cursor: "pointer", display: "flex", alignItems: "center", gap: 4 }}
                     onMouseEnter={e => e.target.style.color = linkHoverColor}
                     onMouseLeave={e => e.target.style.color = linkColor}
                   >
                     {link.label}
                     <ChevronDown size={14} style={{ transition: "transform 0.25s", transform: solutionsOpen ? "rotate(180deg)" : "rotate(0deg)" }} />
                   </button>
                   {solutionsOpen && <SolutionsMenu onClose={() => setSolutionsOpen(false)} onMouseEnter={openSolutions} onMouseLeave={scheduleCloseSolutions} />}
                 </div>
               ) : link.hasDropdown ? (
                <div key={link.path} style={{ position: "relative" }} onMouseEnter={openLocations} onMouseLeave={scheduleCloseLocations} onClick={e => e.stopPropagation()}>
                  <button
                    onClick={() => setLocationsOpen(!locationsOpen)}
                    style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 14, fontWeight: 400, color: linkColor, textDecoration: "none", transition: "color 0.2s", background: "none", border: "none", cursor: "pointer", display: "flex", alignItems: "center", gap: 4 }}
                    onMouseEnter={e => e.target.style.color = linkHoverColor}
                    onMouseLeave={e => e.target.style.color = linkColor}
                  >
                    {link.label}
                    <ChevronDown size={14} style={{ transition: "transform 0.25s", transform: locationsOpen ? "rotate(180deg)" : "rotate(0deg)" }} />
                  </button>
                  {locationsOpen && <LocationsMegaMenu onClose={() => setLocationsOpen(false)} onMouseEnter={openLocations} onMouseLeave={scheduleCloseLocations} />}
                </div>
              ) : link.hasResourcesDropdown ? (
                <div key={link.path} style={{ position: "relative" }} onMouseEnter={openResources} onMouseLeave={scheduleCloseResources} onClick={e => e.stopPropagation()}>
                  <button
                    onClick={() => setResourcesOpen(!resourcesOpen)}
                    style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 14, fontWeight: 400, color: linkColor, textDecoration: "none", transition: "color 0.2s", background: "none", border: "none", cursor: "pointer", display: "flex", alignItems: "center", gap: 4 }}
                    onMouseEnter={e => e.target.style.color = linkHoverColor}
                    onMouseLeave={e => e.target.style.color = linkColor}
                  >
                    {link.label}
                    <ChevronDown size={14} style={{ transition: "transform 0.25s", transform: resourcesOpen ? "rotate(180deg)" : "rotate(0deg)" }} />
                  </button>
                  {resourcesOpen && <ResourcesMegaMenu onClose={() => setResourcesOpen(false)} onMouseEnter={openResources} onMouseLeave={scheduleCloseResources} />}
                </div>
              ) : link.hasMoreDropdown ? (
                <div key={link.path} style={{ position: "relative" }} onMouseEnter={openMore} onMouseLeave={scheduleCloseMore} onClick={e => e.stopPropagation()}>
                  <button
                    onClick={() => setMoreOpen(!moreOpen)}
                    style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 14, fontWeight: 400, color: linkColor, textDecoration: "none", transition: "color 0.2s", background: "none", border: "none", cursor: "pointer", display: "flex", alignItems: "center", gap: 4 }}
                    onMouseEnter={e => e.target.style.color = linkHoverColor}
                    onMouseLeave={e => e.target.style.color = linkColor}
                  >
                    {link.label}
                    <ChevronDown size={14} style={{ transition: "transform 0.25s", transform: moreOpen ? "rotate(180deg)" : "rotate(0deg)" }} />
                  </button>
                  {moreOpen && <MoreMegaMenu onClose={() => setMoreOpen(false)} onMouseEnter={openMore} onMouseLeave={scheduleCloseMore} />}
                </div>
              ) : (
                <Link
                  key={link.path}
                  to={link.path}
                  style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 14, fontWeight: 400, color: linkColor, textDecoration: "none", transition: "color 0.2s" }}
                  onMouseEnter={e => e.target.style.color = linkHoverColor}
                  onMouseLeave={e => e.target.style.color = linkColor}
                >
                  {link.label}
                </Link>
              )
            )}
            <button
              onClick={() => setShowBookCall(true)}
              style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 14, fontWeight: 400, color: linkColor, background: "none", border: "none", cursor: "pointer", transition: "color 0.2s" }}
              onMouseEnter={e => e.target.style.color = linkHoverColor}
              onMouseLeave={e => e.target.style.color = linkColor}
            >
              Book a Call
            </button>
          </div>
        </div>

        {/* Right buttons - desktop */}
        <div style={{ display: "flex", alignItems: "center", gap: 16 }} className="nav-right-desktop">
          {user ? (
            <div style={{ position: "relative" }} onMouseEnter={openAccount} onMouseLeave={scheduleCloseAccount}>
              <button style={{ display: "flex", alignItems: "center", gap: 6, fontFamily: "'DM Sans', sans-serif", fontSize: 14, color: loginColor, background: "none", border: "none", cursor: "pointer", transition: "color 0.2s" }} className="nav-login-desktop">
                <UserCircle size={18} color={loginColor} />
                Account
                <ChevronDown size={13} style={{ transition: "transform 0.2s", transform: accountOpen ? "rotate(180deg)" : "rotate(0deg)" }} />
              </button>
              {accountOpen && (
                <div style={{ position: "absolute", top: "calc(100% + 8px)", right: 0, background: "#fff", borderRadius: 12, boxShadow: "0 8px 32px rgba(0,0,0,0.12)", border: "1px solid #E5E5E5", minWidth: 180, zIndex: 500, padding: "6px 0", animation: "fadeIn 0.15s ease" }}>
                  {user.role === "admin" && (
                    <Link to="/analytics" onClick={() => setAccountOpen(false)} style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 16px", fontFamily: "'DM Sans', sans-serif", fontSize: 14, color: "#1A1A1A", textDecoration: "none", transition: "background 0.15s" }} onMouseEnter={e => e.currentTarget.style.background = "#F7F7F7"} onMouseLeave={e => e.currentTarget.style.background = "transparent"}>
                      <BarChart2 size={15} color="#555" /> Analytics
                    </Link>
                  )}
                  {user.role === "admin" && (
                    <Link to="/revenue-estimate" onClick={() => setAccountOpen(false)} style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 16px", fontFamily: "'DM Sans', sans-serif", fontSize: 14, color: "#1A1A1A", textDecoration: "none", transition: "background 0.15s" }} onMouseEnter={e => e.currentTarget.style.background = "#F7F7F7"} onMouseLeave={e => e.currentTarget.style.background = "transparent"}>
                      <FileText size={15} color="#555" /> Revenue Estimate
                    </Link>
                  )}
                  {user.role === "admin" && (
                    <Link to="/form-submissions" onClick={() => setAccountOpen(false)} style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 16px", fontFamily: "'DM Sans', sans-serif", fontSize: 14, color: "#1A1A1A", textDecoration: "none", transition: "background 0.15s" }} onMouseEnter={e => e.currentTarget.style.background = "#F7F7F7"} onMouseLeave={e => e.currentTarget.style.background = "transparent"}>
                      <Inbox size={15} color="#555" /> Form Submissions
                    </Link>
                  )}
                  {user.role === "admin" && (
                    <Link to="/listings-admin" onClick={() => setAccountOpen(false)} style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 16px", fontFamily: "'DM Sans', sans-serif", fontSize: 14, color: "#1A1A1A", textDecoration: "none", transition: "background 0.15s" }} onMouseEnter={e => e.currentTarget.style.background = "#F7F7F7"} onMouseLeave={e => e.currentTarget.style.background = "transparent"}>
                      <Home size={15} color="#555" /> Listings
                    </Link>
                  )}
                  <Link to="/support" onClick={() => setAccountOpen(false)} style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 16px", fontFamily: "'DM Sans', sans-serif", fontSize: 14, color: "#1A1A1A", textDecoration: "none", transition: "background 0.15s" }} onMouseEnter={e => e.currentTarget.style.background = "#F7F7F7"} onMouseLeave={e => e.currentTarget.style.background = "transparent"}>
                    <Settings size={15} color="#555" /> Settings
                  </Link>
                  <div style={{ height: 1, background: "#F0F0F0", margin: "4px 0" }} />
                  <button onClick={() => base44.auth.logout("/")} style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 16px", fontFamily: "'DM Sans', sans-serif", fontSize: 14, color: "#C4C024", background: "none", border: "none", cursor: "pointer", width: "100%", transition: "background 0.15s" }} onMouseEnter={e => e.currentTarget.style.background = "#E8EDFF"} onMouseLeave={e => e.currentTarget.style.background = "transparent"}>
                    <LogOut size={15} color="#C4C024" /> Logout
                  </button>
                </div>
              )}
            </div>
          ) : (
            <Link to="/login" style={{ display: "flex", alignItems: "center", gap: 6, fontFamily: "'DM Sans', sans-serif", fontSize: 14, color: loginColor, textDecoration: "none", transition: "color 0.2s" }} className="nav-login-desktop">
              <UserCircle size={18} color={loginColor} />
              Login
            </Link>
          )}
          <button
            onClick={() => { base44.analytics.track({ eventName: "get_valuation_click", properties: { location: "header" } }); isGuaranteedRent ? setShowGrModal(true) : setShowModal(true); }}
            style={{
              background: "hsl(var(--primary))", color: "#fff", borderRadius: 8,
              padding: "10px 20px", fontFamily: "'DM Sans', sans-serif",
              fontSize: 14, fontWeight: 600, textDecoration: "none",
              transition: "background 0.2s",
              border: "none",
              cursor: "pointer",
              display: "inline-flex",
              alignItems: "center",
              gap: 6,
            }}
            onMouseEnter={e => e.target.style.background = "hsl(var(--primary) / 0.85)"}
            onMouseLeave={e => e.target.style.background = "hsl(var(--primary))"}
          >
            <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>Get Valuation <ArrowRight size={15} /></span>
          </button>
        </div>

        {/* Mobile menu button */}
        <button
          onClick={(e) => {
            e.stopPropagation();
            setMobileMenuOpen(!mobileMenuOpen);
          }}
          style={{
            display: "none",
            background: "none",
            border: "none",
            cursor: "pointer",
            padding: 8,
          }}
          className="mobile-menu-btn"
          aria-label={mobileMenuOpen ? "Close menu" : "Open menu"}
        >
          {mobileMenuOpen ? (
            <X size={24} color="#1A1A1A" />
          ) : (
            <Menu size={24} color={mobileIconColor} />
          )}
        </button>
      </div>

      {/* Mobile menu */}
      {mobileMenuOpen && createPortal(
        <div
          className="mobile-menu-fullscreen"
          onClick={(e) => e.stopPropagation()}
          style={{ position: "fixed", inset: 0, top: 0, left: 0, right: 0, zIndex: 300, background: "#fff", display: "flex", flexDirection: "column", animation: "slideDown 0.25s cubic-bezier(0.16, 1, 0.3, 1)" }}
        >
          {/* Top bar */}
          <div style={{ maxWidth: 1400, margin: "0 auto", height: 68, display: "flex", alignItems: "center", justifyContent: "space-between", paddingLeft: "clamp(16px, 5vw, 40px)", paddingRight: "clamp(16px, 5vw, 40px)", borderBottom: "1px solid #F0F0F0", flexShrink: 0, width: "100%", boxSizing: "border-box" }}>
            <Link to="/" onClick={() => setMobileMenuOpen(false)} style={{ textDecoration: "none" }}>
              <img
                src={LOGO_DARK}
                alt="Truestays"
                width="105"
                height="26"
                style={{ height: 26, width: "auto" }}
              />
            </Link>
            <button
              onClick={() => setMobileMenuOpen(false)}
              style={{ background: "#F5F5F5", border: "none", borderRadius: 10, width: 36, height: 36, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}
              aria-label="Close menu"
            >
              <X size={18} color="#1A1A1A" />
            </button>
          </div>

          {/* Links */}
          <div style={{ maxWidth: 1400, margin: "0 auto", flex: 1, overflowY: "auto", padding: "8px clamp(16px, 5vw, 40px)", width: "100%", boxSizing: "border-box" }}>
            {navLinks.map((link) =>
              link.hasSolutionsDropdown ? (
                 <div key={link.path}>
                   <button
                     onClick={(e) => { e.stopPropagation(); setMobileSolutionsOpen(!mobileSolutionsOpen); }}
                     style={{ width: "100%", display: "flex", alignItems: "center", justifyContent: "space-between", fontFamily: "'DM Sans', sans-serif", fontSize: 16, fontWeight: 500, color: "#1A1A1A", background: "none", border: "none", cursor: "pointer", padding: "18px 0", textAlign: "left" }}
                   >
                     <span>{link.label}</span>
                     <ChevronRight size={18} color="#1A1A1A" style={{ transition: "transform 0.3s", transform: mobileSolutionsOpen ? "rotate(90deg)" : "rotate(0deg)" }} />
                   </button>
                   {mobileSolutionsOpen && (
                     <div style={{ paddingBottom: 12, paddingLeft: 12, borderLeft: "1px solid #E5E5E5", display: "flex", flexDirection: "column", gap: 2 }}>
                       {FEATURE_MENU.map((item) => {
                         const Icon = SOLUTION_ICONS[item.icon] || Home;
                         return (
                           <Link key={item.slug} to={`/management/${item.slug}`} onClick={() => setMobileMenuOpen(false)} style={mobileRowStyle} onMouseEnter={e => e.currentTarget.style.background = "#F7F7F5"} onMouseLeave={e => e.currentTarget.style.background = "transparent"}>
                             <span style={mobileIconBox}><Icon size={18} color="#555" strokeWidth={1.8} /></span>
                             {item.label}
                           </Link>
                         );
                       })}
                       <Link to="/interiors" onClick={() => setMobileMenuOpen(false)} style={mobileRowStyle} onMouseEnter={e => e.currentTarget.style.background = "#F7F7F5"} onMouseLeave={e => e.currentTarget.style.background = "transparent"}>
                         <span style={mobileIconBox}><Sofa size={18} color="#555" strokeWidth={1.8} /></span>
                         <span>Interiors & Staging</span>
                         <span style={{ marginLeft: "auto", fontFamily: "'DM Sans', sans-serif", fontSize: 10, fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase", color: "#fff", background: "#112215", borderRadius: 100, padding: "2px 8px" }}>Add-on</span>
                       </Link>
                       </div>
                   )}
                 </div>
               ) : link.label === "Locations" ? (
                <div key={link.path}>
                  <button
                    onClick={(e) => { e.stopPropagation(); setMobileLocationsOpen(!mobileLocationsOpen); }}
                    style={{ width: "100%", display: "flex", alignItems: "center", justifyContent: "space-between", fontFamily: "'DM Sans', sans-serif", fontSize: 16, fontWeight: 500, color: "#1A1A1A", background: "none", border: "none", cursor: "pointer", padding: "18px 0", textAlign: "left" }}
                  >
                    <span>{link.label}</span>
                    <ChevronRight size={18} color="#1A1A1A" style={{ transition: "transform 0.3s", transform: mobileLocationsOpen ? "rotate(90deg)" : "rotate(0deg)" }} />
                  </button>
                  {mobileLocationsOpen && (
                    <div style={{ paddingBottom: 12, paddingLeft: 12, borderLeft: "1px solid #E5E5E5" }}>
                      <MobileLocationsAccordion onClose={() => { setMobileLocationsOpen(false); setMobileMenuOpen(false); }} />
                    </div>
                  )}
                </div>
              ) : link.hasMoreDropdown ? (
                <div key={link.path}>
                  <button
                    onClick={(e) => { e.stopPropagation(); setMobileMoreOpen(!mobileMoreOpen); }}
                    style={{ width: "100%", display: "flex", alignItems: "center", justifyContent: "space-between", fontFamily: "'DM Sans', sans-serif", fontSize: 16, fontWeight: 500, color: "#1A1A1A", background: "none", border: "none", cursor: "pointer", padding: "18px 0", textAlign: "left" }}
                  >
                    <span>{link.label}</span>
                    <ChevronRight size={18} color="#1A1A1A" style={{ transition: "transform 0.3s", transform: mobileMoreOpen ? "rotate(90deg)" : "rotate(0deg)" }} />
                  </button>
                  {mobileMoreOpen && (
                    <div style={{ paddingBottom: 12, paddingLeft: 12, borderLeft: "1px solid #E5E5E5", display: "flex", flexDirection: "column", gap: 2 }}>
                      {moreLinksMobile.map((ml) => {
                        const Icon = ml.icon;
                        return (
                          <Link
                            key={ml.path}
                            to={ml.path}
                            onClick={() => setMobileMenuOpen(false)}
                            style={mobileRowStyle}
                            onMouseEnter={e => e.currentTarget.style.background = "#F7F7F5"}
                            onMouseLeave={e => e.currentTarget.style.background = "transparent"}
                          >
                            {Icon && <span style={mobileIconBox}><Icon size={18} color="#555" strokeWidth={1.8} /></span>}
                            {ml.label}
                          </Link>
                        );
                      })}
                    </div>
                  )}
                </div>
              ) : (
                <Link
                  key={link.path}
                  to={link.path}
                  onClick={() => setMobileMenuOpen(false)}
                  style={{ display: "flex", alignItems: "center", justifyContent: "space-between", fontFamily: "'DM Sans', sans-serif", fontSize: 16, fontWeight: 500, color: "#1A1A1A", textDecoration: "none", padding: "18px 0", transition: "color 0.2s" }}
                  onMouseEnter={e => e.target.style.color = "#1A1A1A"}
                  onMouseLeave={e => e.target.style.color = "#1A1A1A"}
                >
                  {link.label}
                </Link>
              )
            )}
            <button
              onClick={(e) => { e.stopPropagation(); setMobileMenuOpen(false); setShowBookCall(true); }}
              style={{ display: "flex", alignItems: "center", justifyContent: "space-between", width: "100%", fontFamily: "'DM Sans', sans-serif", fontSize: 16, fontWeight: 500, color: "#1A1A1A", background: "none", border: "none", cursor: "pointer", padding: "18px 0", textAlign: "left" }}
            >
              <span>Book a Call</span>
              <ChevronRight size={18} color="#1A1A1A" />
            </button>
          </div>

          {/* Footer buttons */}
          <div style={{ maxWidth: 1400, margin: "0 auto", display: "flex", flexDirection: "column", gap: 8, padding: "20px clamp(16px, 5vw, 40px)", borderTop: "1px solid #F0F0F0", flexShrink: 0, width: "100%", boxSizing: "border-box" }} className="mobile-menu-footer">
            {user ? (
              <>
                {user.role === "admin" && (
                  <div>
                    <button
                      onClick={(e) => { e.stopPropagation(); setMobileAdminOpen(!mobileAdminOpen); }}
                      style={{ width: "100%", display: "flex", alignItems: "center", justifyContent: "space-between", fontFamily: "'DM Sans', sans-serif", fontSize: 15, fontWeight: 500, color: "#1A1A1A", background: "none", border: "none", cursor: "pointer", padding: "12px 14px", textAlign: "left" }}
                    >
                      <span style={{ display: "flex", alignItems: "center", gap: 10 }}>
                        <span style={mobileIconBox}><BarChart2 size={18} color="#555" /></span> Admin
                      </span>
                      <ChevronRight size={18} color="#1A1A1A" style={{ transition: "transform 0.3s", transform: mobileAdminOpen ? "rotate(90deg)" : "rotate(0deg)" }} />
                    </button>
                    {mobileAdminOpen && (
                      <div style={{ paddingBottom: 6, paddingLeft: 12, borderLeft: "1px solid #E5E5E5", display: "flex", flexDirection: "column", gap: 2 }}>
                        <Link to="/analytics" onClick={() => setMobileMenuOpen(false)} style={{ ...mobileRowStyle, padding: "10px 12px" }} onMouseEnter={e => e.currentTarget.style.background = "#F7F7F5"} onMouseLeave={e => e.currentTarget.style.background = "transparent"}>
                          <span style={mobileIconBox}><BarChart2 size={18} color="#555" /></span> Analytics
                        </Link>
                        <Link to="/revenue-estimate" onClick={() => setMobileMenuOpen(false)} style={{ ...mobileRowStyle, padding: "10px 12px" }} onMouseEnter={e => e.currentTarget.style.background = "#F7F7F5"} onMouseLeave={e => e.currentTarget.style.background = "transparent"}>
                          <span style={mobileIconBox}><FileText size={18} color="#555" /></span> Revenue Estimate
                        </Link>
                        <Link to="/form-submissions" onClick={() => setMobileMenuOpen(false)} style={{ ...mobileRowStyle, padding: "10px 12px" }} onMouseEnter={e => e.currentTarget.style.background = "#F7F7F5"} onMouseLeave={e => e.currentTarget.style.background = "transparent"}>
                          <span style={mobileIconBox}><Inbox size={18} color="#555" /></span> Form Submissions
                        </Link>
                        <Link to="/listings-admin" onClick={() => setMobileMenuOpen(false)} style={{ ...mobileRowStyle, padding: "10px 12px" }} onMouseEnter={e => e.currentTarget.style.background = "#F7F7F5"} onMouseLeave={e => e.currentTarget.style.background = "transparent"}>
                          <span style={mobileIconBox}><Home size={18} color="#555" /></span> Listings
                        </Link>
                      </div>
                    )}
                  </div>
                )}
                <Link to="/support" onClick={() => setMobileMenuOpen(false)} style={{ ...mobileRowStyle, padding: "12px 14px" }} onMouseEnter={e => e.currentTarget.style.background = "#F7F7F7"} onMouseLeave={e => e.currentTarget.style.background = "transparent"}>
                  <span style={mobileIconBox}><Settings size={18} color="#555" /></span> Settings
                </Link>
                <button
                  onClick={() => base44.auth.logout("/")}
                  style={{ display: "flex", alignItems: "center", gap: 10, fontFamily: "'DM Sans', sans-serif", fontSize: 15, fontWeight: 600, color: "#C4C024", background: "none", border: "none", cursor: "pointer", padding: "12px 14px", borderRadius: 8, transition: "background 0.15s" }}
                  onMouseEnter={e => e.currentTarget.style.background = "#E8EDFF"}
                  onMouseLeave={e => e.currentTarget.style.background = "transparent"}
                >
                  <span style={mobileIconBox}><LogOut size={18} color="#C4C024" /></span> Logout
                </button>
                <button
                  onClick={() => { base44.analytics.track({ eventName: "get_valuation_click", properties: { location: "mobile_menu" } }); setMobileMenuOpen(false); isGuaranteedRent ? setShowGrModal(true) : setShowModal(true); }}
                  style={{ marginTop: 4, background: "hsl(var(--primary))", color: "#fff", border: "none", borderRadius: 10, padding: "14px", fontFamily: "'DM Sans', sans-serif", fontSize: 15, fontWeight: 600, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: 8, transition: "background 0.2s" }}
                  onMouseEnter={e => e.currentTarget.style.background = "#C4C024"}
                  onMouseLeave={e => e.currentTarget.style.background = "hsl(var(--primary))"}
                >
                  Get Valuation
                </button>
              </>
            ) : (
              <>
                <Link
                  to="/login"
                  onClick={() => setMobileMenuOpen(false)}
                  style={{ flex: 1, background: "transparent", color: "#1A1A1A", border: "1.5px solid #E5E5E5", borderRadius: 10, padding: "14px", fontFamily: "'DM Sans', sans-serif", fontSize: 15, fontWeight: 600, textAlign: "center", textDecoration: "none", display: "flex", alignItems: "center", justifyContent: "center", gap: 8 }}
                >
                  <UserCircle size={15} color="#1A1A1A" /> Host Login
                </Link>
                <button
                  onClick={() => { base44.analytics.track({ eventName: "get_valuation_click", properties: { location: "mobile_menu" } }); setMobileMenuOpen(false); isGuaranteedRent ? setShowGrModal(true) : setShowModal(true); }}
                  style={{ flex: 1, background: "hsl(var(--primary))", color: "#fff", border: "none", borderRadius: 10, padding: "14px", fontFamily: "'DM Sans', sans-serif", fontSize: 15, fontWeight: 600, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: 8, transition: "background 0.2s" }}
                  onMouseEnter={e => e.currentTarget.style.background = "#C4C024"}
                  onMouseLeave={e => e.currentTarget.style.background = "hsl(var(--primary))"}
                >
                  Get Valuation
                </button>
              </>
            )}
          </div>
        </div>,
        document.body
      )}

      <style>{`
        @media (max-width: 768px) {
          .nav-links-desktop { display: none !important; }
          .nav-login-desktop { display: none !important; }
          .nav-right-desktop { display: none !important; }
          .mobile-menu-btn { display: block !important; }
        }
        @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
        @keyframes slideDown {
          from {
            opacity: 0;
            transform: translateY(-10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
      `}</style>

      {showModal && (
        <EnrichmentModal
          mode="full"
          onClose={() => setShowModal(false)}
          onSubmitted={() => setShowModal(false)}
        />
      )}

      {showBookCall && (
        <BookCallModal
          onClose={() => setShowBookCall(false)}
          onGetValuation={() => { setShowBookCall(false); setShowModal(true); }}
        />
      )}

      {showGrModal && (
        <GuaranteedRentClosedModal onClose={() => setShowGrModal(false)} />
      )}
    </nav>
  );
}